import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Skeleton,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import { adminColors, radius, TOUCH_HIT_SX } from '../../../theme/adminTheme';

// Same fill STICKY_HEAD_CELL_SX used across AdminDashboardV2.js's tables —
// MUI's `stickyHeader` pins each header cell via `position: sticky`, but its
// default fill is theme.palette.background.default (the page grey), a
// visible mismatch against the white rows scrolling underneath it. Kept
// as its own constant here (not imported from AdminDashboardV2.js, which
// doesn't export it) so this primitive has no dependency on that file —
// the whole point of extracting shared UI out from under it.
const STICKY_HEAD_CELL_SX = { backgroundColor: '#fff' };

// Server-driven, selectable data table shared across the admin console's
// list views (Students, Classes, Safety, ...). "Server-driven" means this
// component never sorts/paginates/filters `rows` itself — `onSortChange`
// just reports the column+direction the caller should refetch with, same
// as `useQueryState`'s `sort`/`order` params are meant to be used.
//
// columns: [{ key, label, render?(row), sortable?, width?, align? }]
// rows: already-sorted/paginated data for the current page
// getRowId(row): defaults to `row.id` — override for rows keyed differently
export default function DataTable({
  columns,
  rows,
  getRowId = (row) => row.id,
  sortKey,
  sortDirection = 'asc',
  onSortChange,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  bulkActions = [],
  onRowClick,
  getRowAriaLabel,
  loading = false,
  skeletonRows = 6,
  emptyState = null,
  dense = false,
  maxHeight,
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = rows.length > 0 && rows.every((row) => selectedSet.has(getRowId(row)));
  const someSelected = !allSelected && rows.some((row) => selectedSet.has(getRowId(row)));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : rows.map(getRowId));
  };

  const toggleRow = (id) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(Array.from(next));
  };

  const handleSortClick = (col) => {
    if (!col.sortable || !onSortChange) return;
    const nextDirection = sortKey === col.key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(col.key, nextDirection);
  };

  const columnCount = columns.length + (selectable ? 1 : 0);
  const showBulkBar = selectable && selectedSet.size > 0;

  return (
    <Box>
      {/* Sticky bulk-action bar — only takes up space once something is
          selected, so the common (nothing selected) state isn't paying rent
          for a bar of empty buttons. */}
      {showBulkBar && (
        <Box
          role="toolbar"
          aria-label={`${selectedSet.size} row${selectedSet.size === 1 ? '' : 's'} selected`}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            mb: 1,
            borderRadius: radius.control,
            bgcolor: adminColors.ink,
            color: '#fff',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {selectedSet.size} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          {bulkActions.map((action) => (
            <Button
              key={action.label}
              size="small"
              onClick={() => action.onClick(Array.from(selectedSet))}
              startIcon={action.icon}
              sx={{
                ...TOUCH_HIT_SX,
                fontWeight: 600,
                color: action.color === 'error' ? '#fca5a5' : '#fff',
                '&:hover': {
                  backgroundColor: action.color === 'error' ? 'rgba(252,165,165,0.12)' : 'rgba(255,255,255,0.1)',
                },
                '&:focus-visible': { outline: `2px solid ${adminColors.accentBorder}`, outlineOffset: -2 },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}

      <TableContainer
        component={Paper}
        sx={{
          maxHeight,
          borderRadius: radius.surface,
          // Horizontal-scroll affordance (admin-console-v2-phase2 review
          // D5): a table with a wide enough row (Invites' Status/Actions
          // columns at 1024px/768px) can scroll sideways with nothing on
          // screen hinting there's more to see. Pure CSS "scroll shadows"
          // technique — two opaque background gradients that mask
          // whatever's scrolled underneath, paired with two thin shadow
          // gradients pinned to the *scrolling content* via
          // `background-attachment: local` so each shadow only renders
          // once there's actually more content in that direction (it
          // scrolls in lockstep with the content, disappearing exactly
          // when there's nothing left to reveal). No JS/ResizeObserver
          // needed to detect overflow — this degrades to "no shadows,
          // still scrollable" on any browser without background-attachment
          // support, never to "not scrollable".
          backgroundImage: `
            linear-gradient(to right, #fff 30%, rgba(255,255,255,0)),
            linear-gradient(to right, rgba(255,255,255,0), #fff 70%) 100% 0,
            linear-gradient(to right, rgba(24,24,27,0.12), rgba(24,24,27,0)),
            linear-gradient(to left, rgba(24,24,27,0.12), rgba(24,24,27,0)) 100% 0
          `,
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#fff',
          backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
          backgroundPosition: '0 0, 100% 0, 0 0, 100% 0',
          backgroundAttachment: 'local, local, scroll, scroll',
        }}
      >
        <Table stickyHeader size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox" sx={STICKY_HEAD_CELL_SX}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    disabled={rows.length === 0}
                    inputProps={{ 'aria-label': 'Select all rows on this page' }}
                    sx={TOUCH_HIT_SX}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{ ...STICKY_HEAD_CELL_SX, width: col.width }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDirection : 'asc'}
                      onClick={() => handleSortClick(col)}
                      sx={{
                        '&:focus-visible': {
                          outline: `2px solid ${adminColors.accent}`,
                          outlineOffset: 2,
                          borderRadius: '4px',
                        },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key -- skeleton placeholder rows have no identity to key by
                <TableRow key={`skeleton-${i}`}>
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Skeleton variant="rounded" width={18} height={18} />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align || 'left'}>
                      <Skeleton variant="text" width={col.width ? Math.min(col.width, 160) : '70%'} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ border: 'none', py: 0 }}>
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = getRowId(row);
                const selected = selectedSet.has(id);
                return (
                  <TableRow
                    key={id}
                    hover={Boolean(onRowClick)}
                    selected={selected}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (e.key === 'Enter') onRowClick(row);
                          }
                        : undefined
                    }
                    aria-label={getRowAriaLabel ? getRowAriaLabel(row) : undefined}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:focus-visible': {
                        outline: `2px solid ${adminColors.accent}`,
                        outlineOffset: -2,
                      },
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleRow(id)}
                          inputProps={{ 'aria-label': `Select row ${id}` }}
                          sx={TOUCH_HIT_SX}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align || 'left'}>
                        {col.render ? col.render(row) : row[col.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
